import { parsePhoneNumber } from 'libphonenumber-js';
import { camelCase } from 'lodash-es';

import { FieldType } from '@components/text-editors/addons/field-mapping';

import { uuid } from './uuid';

export const formatPhoneNumber = (phone: string) => {
  try {
    const phoneNumber = parsePhoneNumber(phone);
    return phoneNumber.formatInternational();
  } catch {
    return phone;
  }
};

export function compareStrings(a: string, b: string, ascending: boolean) {
  if (ascending) {
    return a.localeCompare(b, 'en', { sensitivity: 'accent' });
  }
  return b.localeCompare(a, 'en', { sensitivity: 'accent' });
}

export function abbreviate(value?: string | null): string {
  if (!value) return '';

  let words = value.split(' ');
  if (words.length === 1) return words[0].charAt(0).toUpperCase();

  return words[0].charAt(0).toUpperCase() + words[words.length - 1].charAt(0).toUpperCase();
}

export function capitalizeFirstLetter(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export const camelize = (data: string): string => {
  return camelCase(data);
};

export const fileNameFromPath = (path: string) => {
  try {
    if (!path.startsWith('http')) {
      path = `http://${path}`;
    }
    const url = new URL(path);
    return decodeURI(url.pathname.split('/').pop() ?? path);
  } catch (e) {
    return path;
  }
};

export const formatTime = (hour: string | number = '0', minute: string | number = '0') => {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

export const startsWith = <T extends string>(
  value: string,
  search: T
): value is `${T}${string}` => {
  // TODO: We might want to overwrite the String type, see:
  // https://github.com/microsoft/TypeScript/issues/46958
  return value.startsWith(search);
};

export const asteriskLabel = (label: string, isRequired = true) => {
  if (!isRequired) return label;
  return label.endsWith('*') ? label : `${label} *`;
};

export const unasteriskLabel = (label: string) => {
  return label.endsWith(' *') ? label.slice(0, -2) : label;
};

export function randomId() {
  return uuid().split('-').join('');
}

export function escapeHtml(html: string) {
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function replaceTemplateString(
  template: string,
  data: Record<string, string>
): string | null {
  // Find the first {{ }} and replace it with the value from data
  const lookupKey = /{{\s*(.*?)\s*}}/.exec(template)?.[1];
  return lookupKey ? data[lookupKey as keyof typeof data] : null;
}

const base64FileTypes: { [key: string]: { ending: string; type: string } } = {
  JVBERi0: { ending: 'pdf', type: 'application/pdf' },
  R0lGODdh: { ending: 'gif', type: 'image/gif' },
  R0lGODlh: { ending: 'gif', type: 'image/gif' },
  iVBORw0KGgo: { ending: 'png', type: 'image/png' },
  '/9j/': { ending: 'jpg', type: 'image/jpg' },
};

const getBase64FileType = (value: string) => {
  let type = undefined;
  if (typeof value === 'string') {
    Object.keys(base64FileTypes).forEach((key) => {
      if ([0, 1].includes(value.indexOf(key.toString()))) {
        // string index 0, stringify index 1
        type = key;
      }
    });
  }
  return type;
};

export const isBase64File = (value: string) => {
  const fileType = getBase64FileType(value);
  return fileType !== undefined;
};

export const getBase64FileContentType = (value: string) => {
  const fileType = getBase64FileType(value);
  if (fileType) {
    return base64FileTypes[fileType].type;
  }
  return undefined;
};

export const getBase64FileEnding = (value: string) => {
  const fileType = getBase64FileType(value);
  if (fileType) {
    return base64FileTypes[fileType].ending;
  }
  return undefined;
};

/**
 * Extracts the triple-quoted docstring from the given Python code.
 * Returns the docstring content or an empty string if no match is found.
 */
export function extractDocstring(pythonCode: string): string {
  const docstringRegex = /"""\s*([\s\S]*?)\s*"""/m;
  const match = pythonCode.match(docstringRegex);
  return match ? match[1] : '';
}

const ALLOWED_VARIABLE_TYPES: FieldType[] = [
  'text',
  'email',
  'phone',
  'date',
  'number',
  'paragraph',
  'array_of_strings',
  'array',
  'boolean',
  'decimal',
  'select',
];

/**
 * Parses the docstring to produce the desired output object.
 * The function looks for the 'Returns:' line and then each line after it that matches:
 *   name (type): label
 */
export function variablesFromDocstring(
  docstring: string
): Record<string, { label: string; type: FieldType }> {
  const lines = docstring.split('\n').map((l) => l.trim());
  const result: Record<string, { label: string; type: FieldType }> = {};

  const returnIndex = lines.indexOf('Returns:');
  if (returnIndex !== -1) {
    lines.slice(returnIndex + 1).forEach((line) => {
      const match = line.match(/^(\S+)\s+\(([^)]+)\):\s*(.*)$/);
      if (match) {
        const [, name, rawType, label] = match;
        const type = ALLOWED_VARIABLE_TYPES.includes(rawType as FieldType)
          ? (rawType as FieldType)
          : 'text';
        result[name] = { type, label };
      }
    });
  }

  return result;
}

let _uniqueCodeCounter = 0;
export const generateUniqueCode = () => {
  const time = Date.now().toString(36);
  const count = (_uniqueCodeCounter++).toString(36);
  return `${time}${count}`;
};

export const slugify = (text: string): string => {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove invalid characters
    .replace(/[-\s]+/g, '_'); // Replace spaces and hyphens with an underscore
};
